import {
    Injector,
    Type,
} from '@angular/core';
import {
    ClientErrorCodes,
    ErrorTranslationKeys,
    IUser,
    IUserProfile,
    LoginPayload,
} from '@models';
import { Logger } from '@services/logger/abstract.logger';
import { TranslatableError } from '@utils/translatable-error';

import { SettingsService } from 'main/app-state/current-user/settings/settings.service';
import { NullableStateManager } from 'main/app-state/generics/state-manager';
import {
    BehaviorSubject,
    combineLatest,
    iif,
    Observable,
    of,
    throwError,
} from 'rxjs';
import {
    catchError,
    filter,
    map,
    switchMap,
    take,
    tap,
} from 'rxjs/operators';

import { CurrentUserAPI } from './current-user.api';
import { CurrentUserService } from './current-user.service';
import { UserAuthState } from './current-user.types';
import {
    FolderAPI,
    FolderManager,
} from './folder';
import { LicenseAPI } from './license/license.api';
import { LicenseManager } from './license/license.manager';
import {
    FolderPermissionsAPI,
    FolderPermissionsManager,
    PermissionsAPI,
    PermissionsManager,
} from './permissions';
import {
    SettingsAPI,
    SettingsManager,
} from './settings';
import {
    ThemeAPI,
    ThemeManager,
} from './theme';

const enum RecoverableErrors {
    UserNotLoggedIn = 'UserNotLoggedIn',
    UserShouldBeLoggedOut = 'UserShouldBeLoggedOut',
}

export class CurrentUserManager extends NullableStateManager<IUser> implements CurrentUserAPI {
    protected _state = new BehaviorSubject<IUser | null>(null);

    private _themeManager: ThemeManager;
    private _folder: FolderManager;
    private _settings: SettingsManager;
    private _permissions: PermissionsManager;
    private _folderPermissions: FolderPermissionsManager;
    private _license: LicenseManager;

    private _currentUserService: CurrentUserService;
    private _settingsService: SettingsService;

    constructor(
        injector: Injector,
    ) {
        super();

        this._currentUserService = injector.get<CurrentUserService>(CurrentUserService);
        this._settingsService = injector.get<SettingsService>(SettingsService);

        // The Injector doesn't work directly with abstract classes, so manual casting to Type<Logger> has to be done.
        // More details: https://github.com/angular/angular/issues/23611
        this._logStateChanges('User', injector.get<Logger>(Logger as Type<Logger>));

        this._themeManager = new ThemeManager(injector, this);
        this._folder = new FolderManager(injector, this);
        this._settings = new SettingsManager(injector, this);
        this._permissions = new PermissionsManager(injector);
        this._license = new LicenseManager(injector, this);
        this._folderPermissions = new FolderPermissionsManager(injector, this._permissions);
    }

    public get theme(): ThemeAPI {
        return this._themeManager;
    }

    public get folder(): FolderAPI {
        return this._folder;
    }

    public get settings(): SettingsAPI {
        return this._settings;
    }

    public get permissions(): PermissionsAPI {
        return this._permissions;
    }

    public get folderPermissions(): FolderPermissionsAPI {
        return this._folderPermissions;
    }

    public get license(): LicenseAPI {
        return this._license;
    }

    public get authState(): UserAuthState {
        if (!this.value) { return UserAuthState.NotLoggedIn; }
        if (!this.value.tenancyName) { return UserAuthState.LoggedInAsHost; }
        return UserAuthState.LoggedInAsTenant;
    }

    public get loggedInAsHost() {
        return this.authState === UserAuthState.LoggedInAsHost;
    }

    /**
     * Refreshes the current user's data and emits after all child managers have a non-null state.
     */
    public fetch() {
        return this._currentUserService.get()
            .pipe(
                switchMap((user) => {
                    if (user) { return of(user as unknown as IUser); }

                    if (this.value) {
                        // Stale state.
                        return throwError(RecoverableErrors.UserShouldBeLoggedOut);
                    }

                    // Normal behaviour. Should skip the initialization of child managers.
                    return throwError(RecoverableErrors.UserNotLoggedIn);
                }),
                tap((user) => this._state.next(user)),
                switchMap(() => this._folder.fetch()),
                switchMap(() => this._permissions.fetch()),
                switchMap(() =>
                    combineLatest([
                        this._folder.value$,
                        this._settings.value$,
                        this._permissions.value$,
                        this._license.value$,
                    ]),
                ),
                filter((allStates) => allStates.every((state) => !!state)),
                take(1),
                map(() => this.value!),
                catchError((error: RecoverableErrors | Error) => {
                    switch (error) {
                        case RecoverableErrors.UserNotLoggedIn:
                            return of(null);
                        case RecoverableErrors.UserShouldBeLoggedOut:
                            return this.logout().pipe(map(_ => null));
                        default:
                            return throwError(error);
                    }
                }),
            );
    }

    public ensureCorrectIdentity() {
        return this._currentUserService.ensureCorrectIdentity();
    }

    public login(payload: LoginPayload) {
        payload.tenancyName = (payload.tenancyName || '').trim();
        payload.usernameOrEmailAddress = (payload.usernameOrEmailAddress || '').trim();

        return this.ensureCorrectIdentity()
            .pipe(
                switchMap(() => this._currentUserService.login(payload)),
                switchMap(() => this.ensureCorrectIdentity()),
                switchMap(() => this.fetch() as Observable<Readonly<IUser>>),
            );
    }

    public logout() {
        return this._currentUserService.logout()
            .pipe(
                switchMap(() => this._permissions.clean()),
                switchMap(() => this._folder.clean()),
                tap(() => this._state.next(null)),
                switchMap(() =>
                    combineLatest([
                        this._folder.value$,
                        this._settings.value$,
                        this._permissions.value$,
                        this._license.value$,
                    ]),
                ),
                filter((allStates) => allStates.every((state) => !state)),
                take(1),
                map(() => true),
            );
    }

    public update(data: IUser) {
        return iif(
            () => !!this.value,

            this._currentUserService
                .update(this.value!.id, data)
                .pipe(
                    switchMap(() => this.fetch()),
                ),

            throwError(new TranslatableError(
                ErrorTranslationKeys.SessionExpired,
                ClientErrorCodes.SessionExpired,
            )),
        );
    }

    public patch(data: Partial<IUserProfile>) {
        return iif(
            () => !!this.value,

            this._currentUserService
                .patch(this.value!.id, data)
                .pipe(
                    switchMap(() => this.fetch()),
                ),

            throwError(new TranslatableError(
                ErrorTranslationKeys.SessionExpired,
                ClientErrorCodes.SessionExpired,
            )),
        );
    }

    public saveLanguage(languageCode: string) {
        return this._settingsService.setLanguage(languageCode)
            .pipe(
                switchMap(() => this.settings.fetch()),
                map(_ => this.value),
            );
    }

    public ensureValidFolders = () =>
        // the list of classic folders is stored in userManager
        this.fetch()
            .pipe(
                switchMap(() => this._folder.fetch()),
                map(_ => true),
            )
}
